import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, ButtonLoading } from '@/components/ui/button'
import { Form, FormField, FormItemLayout } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/view/auth-view'

import { authForgotPassword } from '@/services/auth/auth.gql'

export const Route = createFileRoute('/auth/forgot-password')({
  component: RouteComponent,
})

function RouteComponent() {
  const [isValid, setIsValid] = useState(false)
  const [email, setEmail] = useState('')

  return (
    <AuthCard
      title={
        isValid
          ? 'Instruksi reset password terkirim ke email'
          : 'Lupa Password? Jangan khawatir'
      }
      desc={
        isValid ? (
          <p>
            Kami telah mengirim instruksi untuk reset password ke email{' '}
            <span className="font-bold underline decoration-dotted">
              {email}
            </span>
          </p>
        ) : (
          'Silahkan input emailmu yang sudah terdaftar, untuk mengirimkan link reset password.'
        )
      }
    >
      {isValid ? (
        <div className="flex justify-end">
          <Button asChild variant="secondary">
            <Link to="/auth/login">Kembali</Link>
          </Button>
        </div>
      ) : (
        <ForgotPasswordForm
          onSuccess={(email) => {
            setIsValid(true)
            setEmail(email)
          }}
        />
      )}
    </AuthCard>
  )
}

/* ---------------------------------- FORM ---------------------------------- */

type ForgotPasswordDto = z.infer<typeof ForgotPasswordDto>
const ForgotPasswordDto = z.object({
  email: z.string().email(),
})

type ForgotPasswordFormProps = {
  onSuccess: (email: string) => void
}

function ForgotPasswordForm({ onSuccess }: ForgotPasswordFormProps) {
  const form = useForm<ForgotPasswordDto>({
    resolver: zodResolver(ForgotPasswordDto),
    defaultValues: getDefaultValues(),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: authForgotPassword.api,
    onSuccess() {
      onSuccess(form.getValues('email'))
    },
  })

  const handleSubmit = form.handleSubmit((data) => {
    mutateAsync({
      req: {
        email: data.email,
      },
    })
  }, console.error)

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <fieldset className="space-y-4" disabled={isPending}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItemLayout label="Email" className="w-full">
                <Input
                  placeholder="Masukkan email"
                  {...field}
                  type="email"
                  required
                />
              </FormItemLayout>
            )}
          />
          <div className="flex justify-end gap-2">
            <Button asChild variant="secondary">
              <Link to={'/auth/login'}>Kembali</Link>
            </Button>
            <ButtonLoading variant="yes" isLoading={isPending}>
              Reset Password
            </ButtonLoading>
          </div>
        </fieldset>
      </form>
    </Form>
  )
}

function getDefaultValues(): ForgotPasswordDto {
  return {
    email: '',
  }
}
